import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

// Service interface - matches API response
export interface Service {
  id: string;
  name: string;
  price: number;
  duration: number;
  description: string;
  gender: string;
  category: string;
  sub_category: string;
  saloon_id: string;
  currency: string;
  image_url: string;
  rating: number;
  tags: string[];
  discount: number;
  service_type: string;
  pricing_plans: {
    basic: number;
    premium: number;
  };
  franchise_id?: string;
  account_id?: string;
  created_date?: string;
  updated_date?: string;
  is_active?: number;
}

// Franchise info interface
export interface FranchiseInfo {
  franchiseId: string;
  userId: string;
  accountId?: string;
}

// State interface
interface ServiceState {
  // Franchise information
  franchiseId: string | null;
  userId: string | null;
  accountId: string | null;
  
  // Service data
  services: Service[];
  isLoading: boolean;
  error: string | null;
}

const initialState: ServiceState = {
  franchiseId: null,
  userId: null,
  accountId: null,
  services: [],
  isLoading: false,
  error: null,
};

const serviceSlice = createSlice({
  name: "service",
  initialState,
  reducers: {
    // Set franchise information
    setFranchiseInfo: (state, action: PayloadAction<FranchiseInfo>) => {
      state.franchiseId = action.payload.franchiseId;
      state.userId = action.payload.userId;
      state.accountId = action.payload.accountId || null;
    },
    
    // Fetch services (trigger saga)
    fetchServices: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    
    // Fetch services success
    fetchServicesSuccess: (state, action: PayloadAction<Service[]>) => {
      state.isLoading = false;
      state.services = action.payload;
      state.error = null;
    },
    
    // Fetch services failure
    fetchServicesFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    
    // Clear service data
    clearServiceData: (state) => {
      state.services = [];
      state.error = null;
    },
    
    // Clear all data (including franchise info)
    clearAllData: (state) => {
      state.franchiseId = null;
      state.userId = null;
      state.accountId = null;
      state.services = [];
      state.isLoading = false;
      state.error = null;
    },
  },
});

export const {
  setFranchiseInfo,
  fetchServices,
  fetchServicesSuccess,
  fetchServicesFailure,
  clearServiceData,
  clearAllData,
} = serviceSlice.actions;

export default serviceSlice;
